/**
 * product-waitlist.js, back-in-stock email capture for sold-out variants.
 *
 * Toggles the waitlist block via the theme's existing `product:variant-change`
 * event (dispatched on document with detail.variant), so there is no polling.
 * On submit it POSTs to Klaviyo's client subscriptions API.
 */
(function () {
  if (window.__productWaitlistInit) return;
  window.__productWaitlistInit = true;

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  var KLAVIYO_REVISION = '2025-04-15';

  function forEachBlock(fn) {
    Array.prototype.forEach.call(document.querySelectorAll('[data-product-waitlist]'), fn);
  }

  // Single source of truth: exactly one of three mutually exclusive states.
  //   'form'    -> form visible, success hidden, error hidden
  //   'success' -> form hidden,  success visible, error hidden (202 only)
  //   'error'   -> form visible, success hidden, error visible (quiet line)
  function setState(block, state, message) {
    var form = block.querySelector('.product-waitlist__form');
    var success = block.querySelector('.product-waitlist__success');
    var error = block.querySelector('.product-waitlist__error');
    if (form) form.hidden = (state === 'success');
    if (success) success.hidden = (state !== 'success');
    if (error) {
      error.hidden = (state !== 'error');
      error.textContent = state === 'error' ? (message || '') : '';
    }
  }

  function resetToForm(block) {
    setLoading(block, false);
    setState(block, 'form');
  }

  function setLoading(block, loading) {
    var btn = block.querySelector('.product-waitlist__button');
    var txt = block.querySelector('.product-waitlist__button-text');
    if (!btn || !txt) return;
    if (loading) {
      btn.disabled = true;
      if (!btn.dataset.label) btn.dataset.label = txt.textContent;
      txt.textContent = 'Sending…';
    } else {
      btn.disabled = false;
      if (btn.dataset.label) txt.textContent = btn.dataset.label;
    }
  }

  function showError(block, message) {
    setLoading(block, false);
    setState(block, 'error', message); // form stays, quiet error line
  }

  // Success is only ever reached from a confirmed 202 (see subscribe()).
  function showSuccess(block) {
    setLoading(block, false);
    setState(block, 'success'); // form fully hidden, only the tick message
  }

  function subscribe(block, email) {
    var companyId = block.getAttribute('data-company-id');
    var listId = block.getAttribute('data-list-id');
    if (!companyId || !listId) {
      showError(block, 'Something went wrong. Please try again.');
      return;
    }

    var payload = {
      data: {
        type: 'subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email: email,
                properties: {
                  waitlist_product: block.getAttribute('data-product-handle'),
                  waitlist_product_title: block.getAttribute('data-product-title'),
                  waitlist_variant: block.getAttribute('data-variant-id')
                }
              }
            }
          }
        },
        relationships: {
          list: { data: { type: 'list', id: listId } }
        }
      }
    };

    // Klaviyo client subscriptions: must be application/vnd.api+json + a revision
    // header. A success is 202 Accepted with an EMPTY body, never parse JSON on it.
    fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(companyId), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/vnd.api+json',
        revision: KLAVIYO_REVISION
      },
      body: JSON.stringify(payload)
    })
      .then(function (res) {
        if (res.status === 202) {
          showSuccess(block); // confirmed accepted
          return;
        }
        // Non-202: surface the real reason (the API returns JSON:API errors as text).
        res.text().then(function (body) {
          console.error('[product-waitlist] Klaviyo subscription failed, HTTP ' + res.status, body);
        }).catch(function () {
          console.error('[product-waitlist] Klaviyo subscription failed, HTTP ' + res.status);
        });
        showError(block, 'Sorry, something went wrong. Please try again.');
      })
      .catch(function (err) {
        // The request never completed: network offline, or a.klaviyo.com blocked
        // by an ad/tracking/content blocker (common). Log the reason.
        console.error('[product-waitlist] Klaviyo request did not complete (network or blocked):', err);
        showError(block, 'Network error. Please check your connection and try again.');
      });
  }

  function onSubmit(e) {
    var form = e.target.closest ? e.target.closest('.product-waitlist__form') : null;
    if (!form) return;
    e.preventDefault();
    var block = form.closest('[data-product-waitlist]');
    if (!block) return;

    var input = form.querySelector('.product-waitlist__input');
    var email = input ? input.value.trim() : '';

    var errorEl = block.querySelector('.product-waitlist__error');
    if (errorEl) { errorEl.hidden = true; errorEl.textContent = ''; }

    if (!EMAIL_RE.test(email)) {
      showError(block, 'Please enter a valid email address.');
      if (input) input.focus();
      return; // no request sent
    }

    setLoading(block, true);
    subscribe(block, email);
  }

  // Submit handling (delegated so it survives section re-renders).
  document.addEventListener('submit', onSubmit);

  /* ---- Modal: open/close, scroll lock, focus management ---- */
  var lastTrigger = null;

  function getModal(block) { return block.querySelector('[data-waitlist-modal]'); }

  function openModal(block) {
    var modal = getModal(block);
    if (!modal) return;
    resetToForm(block);
    var input = block.querySelector('.product-waitlist__input');
    if (input) input.value = '';
    lastTrigger = block.querySelector('[data-waitlist-open]');
    modal.hidden = false;
    document.documentElement.classList.add('wl-modal-open');
    document.addEventListener('keydown', onKeydown);
    // Focus the email input on open.
    if (input) window.setTimeout(function () { try { input.focus(); } catch (e) {} }, 20);
  }

  // returnFocus=false when closing because the trigger is being hidden (variant switch).
  function closeModal(block, returnFocus) {
    var modal = getModal(block);
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    document.documentElement.classList.remove('wl-modal-open');
    document.removeEventListener('keydown', onKeydown);
    if (returnFocus !== false && lastTrigger) { try { lastTrigger.focus(); } catch (e) {} }
  }

  function openModalEl() { return document.querySelector('[data-waitlist-modal]:not([hidden])'); }

  function closeAny() {
    forEachBlock(function (block) { if (getModal(block) && !getModal(block).hidden) closeModal(block, true); });
  }

  function onKeydown(e) {
    if (e.key === 'Escape' || e.keyCode === 27) { e.preventDefault(); closeAny(); return; }
    if (e.key === 'Tab' || e.keyCode === 9) { trapFocus(e); }
  }

  function trapFocus(e) {
    var modal = openModalEl();
    if (!modal) return;
    var nodes = modal.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    var f = Array.prototype.filter.call(nodes, function (el) { return !el.disabled && el.offsetParent !== null; });
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  // Delegated open/close clicks (X, overlay, trigger).
  document.addEventListener('click', function (e) {
    var opener = e.target.closest ? e.target.closest('[data-waitlist-open]') : null;
    if (opener) {
      var b = opener.closest('[data-product-waitlist]');
      if (b) openModal(b);
      return;
    }
    var closer = e.target.closest ? e.target.closest('[data-waitlist-close]') : null;
    if (closer) {
      var b2 = closer.closest('[data-product-waitlist]');
      if (b2) closeModal(b2, true);
    }
  });

  function inSection(el, sectionId) {
    return !sectionId || el.getAttribute('data-section') === String(sectionId);
  }

  // Toggle visibility on the theme's variant-change event (no polling).
  // When the variant is unavailable the waitlist REPLACES the buy controls:
  // the standard controls ([data-soldout-hide]) hide and the waitlist shows;
  // when available it is the reverse. Both restore correctly on switch.
  document.addEventListener('product:variant-change', function (e) {
    var detail = e.detail || {};
    var variant = detail.variant;
    var sectionId = detail.sectionId;

    // No matching variant (fully unavailable combo) counts as unavailable.
    var available = !!(variant && variant.available);

    // Standard buy controls: qty + Sold out button + payment icons + countdown.
    Array.prototype.forEach.call(document.querySelectorAll('[data-soldout-hide]'), function (el) {
      if (!inSection(el, sectionId)) return;
      el.hidden = !available;
    });

    forEachBlock(function (block) {
      if (!inSection(block, sectionId)) return;
      if (variant && variant.id != null) {
        block.setAttribute('data-variant-id', variant.id);
      }
      block.hidden = available;
      if (available) {
        // Trigger is going away; close any open modal without returning focus to it.
        closeModal(block, false);
      } else {
        resetToForm(block);
      }
    });
  });
})();
